import { useState, useEffect, useCallback } from 'react';
import { Download, Layers, Globe, Monitor } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LAYER_COLORS = {
  pi_check: '#8B5CF6', pi_craft: '#06B6D4', token_optimizer: '#10B981',
  pi_cast: '#F59E0B', pi_loop: '#EC4899', pi_store: '#3B82F6',
};

export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);

  const fetchUsage = useCallback(() => {
    fetch(`${API}/api/admin/usage?days=${days}`, { credentials: 'include' }).then(r => r.json()).then(setData).catch(() => {});
  }, [days]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  if (!data) return <div className="text-white/50">Loading analytics...</div>;

  const maxDAU = Math.max(...data.daily_data.map(d => d.dau), 1);
  const totals = data.totals || {};
  const sources = data.source_totals || {};
  const platforms = data.platform_totals || {};
  const domains = data.top_domains || [];
  const layers = data.layers || {};

  const exportCSV = () => {
    const headers = 'Date,New Users,DAU,Total,PI Check,PI Craft,Token Opt,PI Cast,PI Loop,Slash Cmds,Web,Extension,Avg/User\n';
    const rows = data.daily_data.map(d => `${d.date},${d.new_users},${d.dau},${d.total_events},${d.pi_checks},${d.pi_craft},${d.token_optimizer},${d.pi_cast},${d.pi_loop},${d.slash_commands},${d.web_events},${d.ext_events},${d.avg_per_user}`).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sendpi_usage_${days}d.csv`; a.click();
  };

  return (
    <div data-testid="admin-usage" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Usage Analytics</h1>
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-lg text-xs ${days === d ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-white/40'}`}>{d}d</button>
          ))}
          <button onClick={exportCSV} className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-white/40 hover:text-white flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
        </div>
      </div>

      {/* Period totals by layer */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(layers).map(([key, meta]) => (
          <div key={key} className="bg-[#0E1830] border border-white/10 rounded-xl p-3">
            <div className="w-2 h-2 rounded-full mb-2" style={{ backgroundColor: meta.color }} />
            <p className="text-white font-bold text-lg">{(totals[key] || 0).toLocaleString()}</p>
            <p className="text-white/40 text-[10px] mt-0.5">{meta.name}</p>
          </div>
        ))}
      </div>

      {/* DAU Chart */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-sm text-white/50 mb-4">Daily Active Users</h2>
        <div className="flex items-end gap-1 h-40">
          {data.daily_data.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center group relative">
              <div className="absolute -top-10 bg-[#0E1830] border border-white/20 px-2 py-1 rounded text-xs text-white hidden group-hover:block z-10 whitespace-nowrap">
                {d.date}: {d.dau} DAU
              </div>
              <div className="w-full bg-gradient-to-t from-cyan-500/60 to-cyan-400/30 rounded-t" style={{ height: `${(d.dau / maxDAU) * 100}%`, minHeight: d.dau > 0 ? '2px' : 0 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Source + Platform row */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /> Source Distribution</h2>
          <div className="space-y-3">
            {[{ label: 'Web App', val: sources.web || 0, color: 'bg-cyan-500' }, { label: 'Chrome Extension', val: sources.chrome_extension || 0, color: 'bg-violet-500' }].map(s => {
              const total = (sources.web || 0) + (sources.chrome_extension || 0);
              const pct = total > 0 ? Math.round(s.val / total * 100) : 0;
              return (
                <div key={s.label}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-white/50">{s.label}</span><span className="text-white/70 font-bold">{s.val} ({pct}%)</span></div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden"><div className={`h-full ${s.color} rounded-full`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Monitor className="w-4 h-4 text-amber-400" /> AI Platform Distribution</h2>
          {Object.keys(platforms).length === 0 ? (
            <p className="text-white/20 text-xs">No platform-tagged events yet</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(platforms).slice(0, 7).map(([name, count]) => {
                const maxP = Math.max(...Object.values(platforms));
                return (
                  <div key={name}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-white/50 capitalize">{name}</span><span className="text-white/70 font-bold">{count}</span></div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${(count / maxP) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Daily table */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-3 py-3 text-white/40 font-medium text-xs">Date</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">New</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">DAU</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Check</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Craft</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Token</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Cast</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Loop</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Web</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Ext</th>
                <th className="text-right px-3 py-3 text-white/40 font-medium text-xs">Avg</th>
              </tr>
            </thead>
            <tbody>
              {data.daily_data.slice(-14).reverse().map(d => (
                <tr key={d.date} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2 text-white/70 text-xs">{d.date}</td>
                  <td className="px-3 py-2 text-right text-white text-xs">{d.new_users}</td>
                  <td className="px-3 py-2 text-right text-cyan-400 text-xs font-bold">{d.dau}</td>
                  <td className="px-3 py-2 text-right text-white/70 text-xs">{d.pi_checks}</td>
                  <td className="px-3 py-2 text-right text-white/70 text-xs">{d.pi_craft}</td>
                  <td className="px-3 py-2 text-right text-white/70 text-xs">{d.token_optimizer}</td>
                  <td className="px-3 py-2 text-right text-white/70 text-xs">{d.pi_cast}</td>
                  <td className="px-3 py-2 text-right text-white/70 text-xs">{d.pi_loop}</td>
                  <td className="px-3 py-2 text-right text-white/50 text-xs">{d.web_events}</td>
                  <td className="px-3 py-2 text-right text-white/50 text-xs">{d.ext_events}</td>
                  <td className="px-3 py-2 text-right text-white/50 text-xs">{d.avg_per_user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top domains */}
      {domains.length > 0 && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3">Top Domains</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {domains.map(d => (
              <div key={d.domain} className="flex justify-between items-center bg-white/[0.03] rounded-lg px-3 py-2">
                <span className="text-white/50 text-xs truncate">{d.domain}</span>
                <span className="text-white/70 text-xs font-bold ml-2">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
